import {
  CARD_CONTENT_VERSION,
  CARD_SLOTS,
  type CardLoadout,
  type CardSlot,
  cardDefinition,
  type PvpCardSlotFrame,
  type PvpSummonFrame,
} from '@kenocyber/shared';
import { type CardAbilityState, cardAbilityCooldownMs } from '../cards/abilities';
import { CARD_CHARGE_FULL_BPS, cardBalance } from '../cards/balance';
import {
  type CardEffectState,
  cardFreezeBlocksAttack,
  cardIsCloaked,
  cardIsFrozen,
  cardIsHittable,
} from '../cards/effects';
import { type CardSlotUiView, cardSlotUiViews } from '../cards/ui';
import { PVP_FIREBALL_CHARGE_MS } from './balance';

/**
 * **Os três espaços de uma cadeira, como o fio os leva** (gate V28H.J).
 *
 * ## Porque existe uma projecção, em vez de mandar o estado
 *
 * Porque o estado do domínio traz coisas que não são da apresentação e não
 * podem sair do servidor: a instância que a conta possui, e o nível dela. Um
 * frame de duelo que os levasse seria um frame a decorar um cofre - a mesma
 * regra que `jewel-presentation.test.ts` guarda para as joias.
 *
 * O que atravessa é o que a barra desenha: qual carta, quanto falta, e de quanto
 * é o arrefecimento total para o anel ter denominador.
 *
 * ## Porque são sempre TRÊS
 *
 * Porque a barra desenha três caixas. Um espaço vazio diz-se com `d: null`, e
 * uma lista que encolhesse obrigaria o cliente a adivinhar quais faltam - a
 * mesma decisão que `cardSlotsFor` tomou no inventário e que `cardSlotUiViews`
 * tomou no HUD.
 */
export function pvpCardSlotsOf(cards: readonly CardAbilityState[]): PvpCardSlotFrame[] {
  return CARD_SLOTS.map((slot, index) => {
    const card = cards.find((entry) => entry.slot === slot) ?? null;
    if (card === null) {
      return { s: slotNumber(index), d: null, a: 0, c: 0, ct: 0 };
    }
    return {
      s: slotNumber(index),
      /*
       * O ID da definição, e não o nome: o cliente conhece o catálogo público
       * das dezasseis cartas e resolve o nome sozinho. Mandar a frase seria
       * mandar texto que já está do outro lado.
       */
      d: cardDefinition(card.definitionId) === null ? null : card.definitionId,
      a: card.activeRemainingMs,
      c: card.cooldownRemainingMs,
      /*
       * O TOTAL do arrefecimento desta carta neste nível.
       *
       * Ele depende do nível, que é inventário e não sai daqui - por isso viaja
       * o resultado, e nunca a razão. Sem ele o anel do HUD não teria
       * denominador e teria de adivinhar um.
       */
      ct: cardAbilityCooldownMs(card),
      /*
       * E o NÍVEL (gate V28H.Q), porque a barra o mostra.
       *
       * Ele é meu e viaja na metade privada desta cadeira, ao lado dos meus
       * arrefecimentos - o adversário nunca o vê. O id da instância continua a
       * não sair daqui: esse é posse, e posse não é assunto de um duelo.
       */
      l: card.level,
    };
  });
}

/** Os três espaços vazios, para uma cadeira que ainda não se sentou. */
export function pvpEmptyCardSlots(): PvpCardSlotFrame[] {
  return pvpCardSlotsOf([]);
}

function slotNumber(index: number): 1 | 2 | 3 {
  return (index + 1) as 1 | 2 | 3;
}

/**
 * O nome canónico de um espaço, a partir do número que o fio leva.
 *
 * O protocolo conta bytes e diz 1, 2 ou 3; o domínio fala pelos nomes que o
 * inventário usa. A tradução vive aqui, uma vez, na fronteira entre os dois -
 * duas cópias dela seriam duas oportunidades de trocar os espaços.
 */
export const PVP_CARD_SLOT_BY_NUMBER: Readonly<Record<1 | 2 | 3, CardSlot>> = Object.freeze({
  1: 'card_slot_1',
  2: 'card_slot_2',
  3: 'card_slot_3',
});

/**
 * **Os três espaços do fio, como o HUD os desenha** (gate V28H.J).
 *
 * A ponte entre o frame do duelo e `cardSlotUiViews`, que é o modelo que a
 * barra já sabe ler - o mesmo que a caça usa. Escrita aqui e não no componente
 * porque é uma tradução entre um protocolo e um modelo, e as duas pontas são
 * do domínio.
 *
 * O que ela NÃO faz é decidir: os estados, as prioridades e os motivos vivem em
 * `cards/ui.ts`, e este ficheiro só lhes entrega o que o fio trouxe.
 */
export function pvpCardSlotViews(
  slots: readonly PvpCardSlotFrame[],
  context: { readonly alive: boolean; readonly inMatch: boolean },
): readonly CardSlotUiView[] {
  return cardSlotUiViews({
    cards: slots.flatMap((slot) =>
      slot.d === null
        ? []
        : [
            {
              slot: PVP_CARD_SLOT_BY_NUMBER[slot.s],
              definitionId: slot.d,
              /*
               * A INSTÂNCIA não atravessa o fio - ela é posse, e posse não é
               * assunto de um duelo. O nível atravessa desde o gate V28H.Q, na
               * metade privada desta cadeira, porque a barra mostra-o e um
               * preenchimento de `1` chegou a ser impresso como "Nível 1" numa
               * carta de nível 6.
               *
               * A ausência lê-se como `1` e não como um erro: um frame de uma
               * ponta anterior descreve um espaço cujo nível ninguém mandou, e o
               * mínimo é a única resposta que não inventa uma carta melhor do
               * que ela é.
               */
              instanceId: '',
              level: slot.l ?? 1,
              cooldownRemainingMs: slot.c,
              activeRemainingMs: slot.a,
              attackAccumulatorMs: 0,
              chargeRemainingMs: 0,
              chargeStrengthBps: CARD_CHARGE_FULL_BPS,
            },
          ],
    ),
    shipAlive: context.alive,
    hasSession: context.inMatch,
    mode: 'pvp',
    /*
     * O total do arrefecimento vem do FIO, e não do catálogo: ele depende do
     * nível, e o nível é inventário que não sai do servidor.
     */
    cooldownTotalsMs: Object.fromEntries(
      slots
        .filter((slot) => slot.d !== null)
        .map((slot) => [PVP_CARD_SLOT_BY_NUMBER[slot.s], slot.ct]),
    ),
  });
}

/**
 * **O loadout congelado de um duelo, a partir do que o bilhete trouxe**
 * (gate V28H.J).
 *
 * ## Porque a construção vive AQUI e não no lado da API
 *
 * Porque `jewel-socket-db.test.ts` proíbe qualquer identificador de POSSE de
 * aparecer em `apps/api/src/pvp/` - e a proibição é boa: o id de uma joia, de
 * uma skin, de um socket ou de uma cópia é assunto do cofre, e o duelo não tem
 * nada que ver com ele.
 *
 * O bilhete do duelo traz três factos por carta - o espaço, a definição e o
 * nível - e mais nada. É esta função que os transforma na forma que a simulação
 * espera, e o campo da cópia fica VAZIO porque não há cópia nenhuma a nomear: a
 * sala activa por espaço, decide a habilidade pela definição, e o arrefecimento
 * pelo nível.
 */
export function pvpCardLoadoutOf(
  entries: readonly {
    readonly slot: CardSlot;
    readonly definitionId: string;
    readonly level: number;
  }[],
): CardLoadout {
  return {
    contentVersion: CARD_CONTENT_VERSION,
    cards: entries.map((entry) => ({
      slot: entry.slot,
      definitionId: entry.definitionId,
      level: entry.level,
      /* Sem cópia: a posse não atravessa para o duelo. Ver a nota acima. */
      instanceId: '',
    })),
  };
}

/**
 * **As duas bandeiras públicas das cartas, como o frame as escreve**
 * (gate V28H.L).
 *
 * Ausentes quando não há nada, que é o estado de quase todo o casco em quase
 * todo o tique: um zero explícito custaria bytes em todos os frames de todas as
 * partidas para dizer o que a ausência já diz.
 *
 * Uma função e não dois `...(x ? {} : {})` escritos à mão em cada sítio: o
 * duelo e o 3x3 escrevem o mesmo frame por casco, e duas cópias seriam dois
 * sítios para a terceira bandeira se esquecer de aparecer.
 */
/**
 * **O que as ranhuras de um casco dizem em público** (gate V28H.X).
 *
 * ## Porque isto é uma função, e não duas leituras iguais
 *
 * Porque já foram duas. O duelo lia as ranhuras dentro da sala e o 3x3 lia-as
 * outra vez no seu próprio ficheiro de fio, com as mesmas seis expressões
 * escritas à mão - e o comentário que estava lá avisava exactamente do que isso
 * custa: "o 3x3 já perdeu `cv`, `cg`, `cw`, as invocações e a bandeira da
 * Bomba por serem escritos duas vezes".
 *
 * O gate V28H.X acrescentou dois estados e a duplicação partiu de imediato - um
 * dos lados compilou sem eles. Agora há uma leitura, os dois protocolos chamam-na
 * e um estado novo entra nos dois modos por construção.
 *
 * O que ela NÃO faz é decidir a forma no fio: isso é {@link pvpCardFlagFields},
 * e a separação é a de sempre - uma diz o que é verdade, a outra diz como isso
 * se escreve.
 */
export function pvpPublicCardFlags(effects: CardEffectState): {
  readonly frozen: boolean;
  readonly untargetable: boolean;
  readonly malware: boolean;
  readonly paralysed: boolean;
  readonly slowed: boolean;
  readonly frostTier: number;
  readonly attackBroken: boolean;
  readonly marked: boolean;
} {
  return {
    frozen: cardIsFrozen(effects),
    untargetable: !cardIsHittable(effects),
    malware: effects.malware.remainingMs > 0,
    paralysed: cardFreezeBlocksAttack(effects),
    slowed: effects.slow.remainingMs > 0,
    /* Um degrau por segundo que falta; o tecto está em quem escreve o campo. */
    frostTier: effects.frozen.remainingMs / 1_000,
    attackBroken: effects.attackBreak.remainingMs > 0,
    marked: effects.damageBreak.remainingMs > 0,
  };
}

export function pvpCardFlagFields(flags: {
  readonly frozen: boolean;
  readonly untargetable: boolean;
  /** **Os quatro estados de debuff** (gate V28H.W), do casco de quem os leva. */
  readonly malware: boolean;
  readonly paralysed: boolean;
  readonly slowed: boolean;
  /**
   * Quanta casca de gelo desenhar, de um a três, ou zero para nenhuma.
   *
   * Derivada do tempo que FALTA - um por segundo restante, com tecto em três -
   * portanto ela desce enquanto o gelo derrete, e quem olha lê quanto tempo o
   * adversário ainda está preso. Ver o campo `ft` no protocolo.
   */
  readonly frostTier: number;
  /** **Os dois do gate V28H.X**: a cadência partida e a marca. */
  readonly attackBroken: boolean;
  readonly marked: boolean;
}): { fz?: 1; bt?: 1; mw?: 1; pz?: 1; lt?: 1; ft?: 1 | 2 | 3; cc?: 1; sk?: 1 } {
  const tier = Math.max(0, Math.min(3, Math.ceil(flags.frostTier)));
  return {
    ...(flags.frozen ? { fz: 1 as const } : {}),
    ...(flags.untargetable ? { bt: 1 as const } : {}),
    ...(flags.malware ? { mw: 1 as const } : {}),
    ...(flags.paralysed ? { pz: 1 as const } : {}),
    ...(flags.slowed ? { lt: 1 as const } : {}),
    ...(tier === 1 || tier === 2 || tier === 3 ? { ft: tier } : {}),
    ...(flags.attackBroken ? { cc: 1 as const } : {}),
    ...(flags.marked ? { sk: 1 as const } : {}),
  };
}

/**
 * **Os dois campos de uma bomba de CARTA em voo** (gate V28H.U).
 *
 * Uma função e não dois espalhados à mão, pela mesma razão que
 * {@link pvpCardFlagFields} é uma: o duelo e o 3x3 publicam o mesmo projétil, e
 * duas cópias seriam dois sítios onde o Catolé podia ficar a desenhar-se como um
 * tiro comum num dos modos.
 *
 * Ausentes quando não é uma bomba de carta, que é quase todo o projétil de quase
 * toda a partida: escrever `cb: null` em cada tiro seria pagar um campo por
 * projétil, vinte vezes por segundo, para dizer o normal.
 */
/** Os corpos que uma carta pode pôr no ar. */
export type PvpCardBodyKind = 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';

/**
 * **Este corpo REBENTA, ou é um dardo que aplica um efeito?** (gate V28H.X).
 *
 * ## Porque isto é uma função, e exaustiva
 *
 * Porque a lista já foi escrita à mão uma vez, no sítio onde um projéctil toca
 * um casco, e o terceiro dardo ficou de fora dela: o raio do Curto-Circuito caía
 * no ramo da explosão - raio zero, dano zero - e **acertava sem fazer nada**.
 * Nada estoirou, nada avisou, e a carta parecia implementada.
 *
 * O `switch` sem `default` é o que impede a repetição: acrescentar um corpo
 * novo a `PvpCardBodyKind` sem decidir aqui o que ele faz deixa de compilar. A
 * decisão passa a ser obrigatória em vez de lembrada.
 */
export function pvpCardBodyDetonates(kind: PvpCardBodyKind): boolean {
  switch (kind) {
    case 'fireball':
    case 'catole':
      return true;
    case 'ice_sphere':
    case 'ice_star':
    case 'shock_bolt':
      return false;
  }
}

export function pvpCardBlastFields(
  blast: {
    readonly kind: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';
    readonly radiusMu: number;
  } | null,
): { cb?: 'f' | 'c' | 'z' | 't' | 's'; br?: number } {
  if (blast === null) {
    return {};
  }
  /*
   * Uma letra por corpo (gates V28H.U, V28H.W e V28H.X): a esfera de fogo, o
   * míssil, a esfera de gelo, a estrela de gelo e o raio elétrico. Cinco coisas
   * desenhadas de cinco maneiras, e a cena escolhe pela letra - nunca pelo raio,
   * que é um número de equilíbrio.
   */
  const cb =
    blast.kind === 'catole'
      ? 'c'
      : blast.kind === 'ice_sphere'
        ? 'z'
        : blast.kind === 'ice_star'
          ? 't'
          : blast.kind === 'shock_bolt'
            ? 's'
            : 'f';
  return { cb, br: blast.radiusMu };
}

/** As letras que um casco pode publicar sobre as suas cartas. */
export type PvpCardVisualLetter = 'o' | 'b' | 'l' | 'c' | 'g' | 'd' | 'h' | 'k' | 'w' | 'm';

/**
 * **O que as cartas de um casco desenham** (gates V28H.L, V28H.N e V28H.O).
 *
 * ## Uma leitura, e não duas
 *
 * Esta decisão esteve escrita DUAS vezes - uma no duelo, outra no 3x3 - e as
 * duas cópias eram idênticas por acidente e não por construção. É assim que
 * duas metades da mesma sala passam a discordar sobre o que está a acontecer.
 * Agora ela vive aqui, e os dois protocolos leem-na.
 *
 * ## O que sai
 *
 * As letras, e dois números. `o` é passivo - basta o Turbilhão estar equipado -
 * e as outras três dizem o que está a acontecer agora. Os números só existem
 * quando fazem sentido: a carga enquanto ela corre, a força enquanto o feixe
 * arde.
 */
export interface PvpCardVisualState {
  readonly letters: PvpCardVisualLetter[];
  /** Quanto da carga já foi cumprido, 0 a 100. `null` quando ninguém carrega. */
  readonly chargePercent: number | null;
  /** Com que força o feixe arde, 0 a 100. `null` quando não há feixe. */
  readonly beamPercent: number | null;
}

export function pvpCardVisualStateOf(
  cards: readonly CardAbilityState[],
  /**
   * **As ranhuras de efeito do casco** (gate V28H.V), quando quem chama as tem.
   *
   * Duas das três letras novas saem daqui e não da carta, e a razão é o Manto: o
   * temporizador que conta é o do EFEITO, porque atacar apaga-o antes do tempo.
   * Ler a carta desenharia a silhueta durante nove segundos mesmo depois de o
   * jogador ter disparado e ficado à vista.
   *
   * Opcional porque nem todo o sítio que projecta letras tem as ranhuras à mão -
   * e a ausência significa "não sei", que produz menos letras e nunca uma letra
   * errada.
   */
  effects?: CardEffectState,
): PvpCardVisualState {
  const letters: PvpCardVisualLetter[] = [];
  if (effects !== undefined) {
    /*
     * O MANTO e a SOBREMARCHA leem-se das ranhuras: são elas que a sala
     * consulta para decidir quem se vê e a que velocidade se anda, portanto são
     * elas que dizem a verdade sobre o que desenhar.
     */
    if (cardIsCloaked(effects)) {
      letters.push('k');
    }
    if (effects.haste.remainingMs > 0) {
      letters.push('h');
    }
  }
  let chargePercent: number | null = null;
  let beamPercent: number | null = null;
  for (const card of cards) {
    /*
     * **O círculo é PASSIVO** (gate V28H.O), e por isso vem antes da pergunta
     * sobre o tempo activo: ele pertence à carta estar equipada, e não a ela
     * estar ligada.
     */
    if (card.definitionId === 'card_blade_storm') {
      letters.push('o');
    }
    if (card.activeRemainingMs <= 0) {
      continue;
    }
    const balance = cardBalance(card.definitionId);
    if (card.chargeRemainingMs > 0) {
      /*
       * **QUAL carga** (gate V28H.U), e não só que alguma corre.
       *
       * As duas desenham coisas diferentes - a Lança acende uma corona no bico,
       * a Esfera faz nascer uma bola que incha à frente do casco - e uma letra
       * só obrigava a cena a escolher uma delas para as duas. Escolhia a corona,
       * e a esfera carregada era invisível até sair.
       *
       * A letra pela carta e não pelo efeito: são estas duas que existem, e o
       * dia em que uma terceira carregar traz a sua letra em vez de herdar uma.
       */
      letters.push(card.definitionId === 'card_charged_fireball' ? 'g' : 'c');
      /*
       * A fracção JÁ CUMPRIDA, e a crescer: é a mesma leitura que a força vai
       * ter quando a tecla se soltar, o que torna a corona uma promessa honesta.
       */
      /*
       * O tempo total é o DESTA arena (gate V28I.C).
       *
       * A Esfera Ígnea carrega quinze segundos aqui e 400 ms na caça, e o
       * catálogo guarda zero de propósito - lê-lo daria uma carga sempre a 100%,
       * ou seja uma bola já cheia no primeiro quadro.
       */
      const total =
        card.definitionId === 'card_charged_fireball'
          ? PVP_FIREBALL_CHARGE_MS
          : (balance?.chargeMs ?? 0);
      chargePercent =
        total <= 0
          ? 100
          : Math.max(
              0,
              Math.min(100, Math.round(((total - card.chargeRemainingMs) * 100) / total)),
            );
      continue;
    }
    if (card.definitionId === 'card_blade_storm') {
      letters.push('b');
    }
    /*
     * O ARRANQUE lê-se da CARTA, ao contrário das outras duas: o avanço dura
     * exactamente o tempo activo dela - 250 ms, cinco tiques - e o empurrão que
     * o executa é o mesmo campo que uma explosão usa, portanto não serve para o
     * distinguir.
     */
    if (card.definitionId === 'card_dash') {
      letters.push('d');
    }
    /*
     * E o MALWARE, enquanto a carta dele correr: é o casco que lançou, e o que
     * ele desenha é o hack a sair - não a infecção, que é da vítima.
     */
    if (card.definitionId === 'card_malware') {
      letters.push('w');
    }
    /*
     * **E a MARCA DA CAVEIRA** (gate V28H.X), pela mesma razão exacta.
     *
     * O dono viu a carta a marcar o adversário sem nada acontecer de quem a
     * lançou: "não há nenhum efeito que sai da nave que disparou o efeito,
     * precisa ter". É o mesmo caso do Malware - o que a vítima leva é dela, e
     * quem lança tem de mostrar que lançou.
     *
     * A carta fica activa os seis segundos do debuff, portanto a letra dura
     * isso; o pulso que a cena desenha é curto e ignora repetições, e é ele que
     * decide quanto tempo se vê.
     */
    if (card.definitionId === 'card_damage_break') {
      letters.push('m');
    }
    if (card.definitionId === 'card_solar_lance') {
      letters.push('l');
      beamPercent = Math.max(
        0,
        Math.min(100, Math.round((card.chargeStrengthBps * 100) / CARD_CHARGE_FULL_BPS)),
      );
    }
  }
  return { letters, chargePercent, beamPercent };
}

/**
 * E os campos que o frame escreve a partir dela.
 *
 * Ausentes quando não há nada, que é o estado de quase todo o casco em quase
 * todo o tique - a mesma disciplina de todos os campos raros deste frame.
 */
export function pvpCardVisualFields(state: PvpCardVisualState): {
  cv?: PvpCardVisualLetter[];
  cg?: number;
  cw?: number;
} {
  return {
    ...(state.letters.length === 0 ? {} : { cv: [...state.letters] }),
    ...(state.chargePercent === null ? {} : { cg: state.chargePercent }),
    ...(state.beamPercent === null ? {} : { cw: state.beamPercent }),
  };
}

/**
 * Uma invocação, do estado da sala para o frame.
 *
 * A vida sai em **basis points do máximo dela**: a cena desenha uma razão, e
 * mandar dois números de quarenta dígitos para o cliente calcular uma fracção
 * seria pagar por uma divisão que a sala já sabe fazer.
 */
export function pvpSummonFrameOf(summon: {
  readonly id: number;
  readonly kind: string;
  readonly ownerParticipantId: string;
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  readonly healthMilli: bigint;
  readonly maxHealthMilli: bigint;
  readonly pilotParticipantId: string | null;
}): PvpSummonFrame {
  const ratio =
    summon.maxHealthMilli <= 0n
      ? 0
      : Number((summon.healthMilli * 10_000n) / summon.maxHealthMilli);
  return {
    i: summon.id,
    k: summon.kind === 'leviathan' ? ('l' as const) : ('d' as const),
    x: summon.x,
    y: summon.y,
    ax: summon.aimX,
    ay: summon.aimY,
    p: summon.ownerParticipantId,
    hp: Math.max(0, Math.min(10_000, ratio)),
    ...(summon.pilotParticipantId === null ? {} : { pl: summon.pilotParticipantId }),
  };
}
